import { __decorate, __metadata } from '../../node_modules/tslib/tslib.es6.js';
import '../../node_modules/lit/node_modules/@lit/reactive-element/reactive-element.js';
import { html as x, nothing as A } from '../../node_modules/lit-html/lit-html.js';
import '../../node_modules/lit-element/lit-element.js';
import { property as n } from '../../node_modules/lit/node_modules/@lit/reactive-element/decorators/property.js';
import '../../node_modules/lit/node_modules/@lit/reactive-element/decorators/query-assigned-elements.js';
import { ifDefined as l } from '../../node_modules/lit-html/directives/if-defined.js';
import getClassName from '../../node_modules/@fmr-ap142296/brick-helpers/dist/src/functions/get-class-name.js';
import { ScopedElementsMixin } from '../utilities/scoped-elements/scoped-elements.mixin.js';
import { PvdSlotifyBaseComponent } from '../pvd-base-component/pvd-slotify-base-component.js';
import { StandardSize, Size } from '../pvd-utils/enums/size.js';

var ButtonType;
(function (ButtonType) {
    ButtonType["button"] = "button";
    ButtonType["submit"] = "submit";
    ButtonType["reset"] = "reset";
})(ButtonType || (ButtonType = {}));
var ButtonVariant;
(function (ButtonVariant) {
    ButtonVariant["primary"] = "primary";
    ButtonVariant["secondary"] = "secondary";
    ButtonVariant["tertiary"] = "tertiary";
    ButtonVariant["invitation"] = "invitation";
    ButtonVariant["trade"] = "trade";
})(ButtonVariant || (ButtonVariant = {}));
const DEFAULT_DISABLED = false;
const DEFAULT_FULL_WIDTH = false;
const DEFAULT_ICON_ONLY = false;
const DEFAULT_SIZE = StandardSize.medium;
const DEFAULT_TYPE = ButtonType.button;
const DEFAULT_VARIANT = ButtonVariant.primary;
const ANCHOR_PROTECTED_ATTR_MAP = new Map([['href', 'pvd-href']]);
const BUTTON_PROTECTED_ATTR_MAP = new Map([
    ['disabled', 'pvd-disabled'],
    ['type', 'pvd-type'],
]);
/**
 * Providence Button component.
 * @see {@link https://providence.fmr.com/4.x/components/button/button.html|Button documentation}
 */
class PvdButton extends ScopedElementsMixin(PvdSlotifyBaseComponent) {
    constructor() {
        super(...arguments);
        //#endregion
        // #region LitElement properties
        this.pvdDisabled = DEFAULT_DISABLED;
        this.pvdFullWidth = DEFAULT_FULL_WIDTH;
        this.pvdIconOnly = DEFAULT_ICON_ONLY;
        this.pvdSize = DEFAULT_SIZE;
        this.pvdType = DEFAULT_TYPE;
        this.pvdVariant = DEFAULT_VARIANT;
        // #endregion
    }
    static get scopedElements() {
        // $namespace-$name-$extendingComponentName
        return {
            'pvd-scoped-icon': customElements.get(`${this.namespace}-icon`),
        };
    }
    // #endregion
    // #region public methods
    click() {
        // [PVD-10179] prevent click event from triggering when Button is disabled
        if (!this.pvdDisabled) {
            super.click();
        }
    }
    //#endregion
    // #region private methods
    get hasIcon() {
        return this.hasIconLeft || this.hasIconRight;
    }
    get hasIconLeft() {
        return !!this.pvdIconLeft || this.isSlotUsed('icon-left');
    }
    get hasIconRight() {
        return !!this.pvdIconRight || this.isSlotUsed('icon-right');
    }
    get hasText() {
        return !!this.pvdText || this.isSlotUsed();
    }
    /**
     * Callback to detect changes to the slotted content
     * @param event
     */
    onSlotChangeHandler(event) {
        this.requestUpdate();
    }
    /**
     * Sets attributes on the button element that are coming from pvdNativeButtonAttributes
     * @param prevAttr optional, if user updates pvdNativeButtonAttributes, this will remove/update existing attributes
     */
    setNativeButtonAttributes(prevAttr) {
        const targetElement = this.querySelector('button');
        this.setNativeAttributes(this.pvdNativeButtonAttributes, BUTTON_PROTECTED_ATTR_MAP, targetElement, prevAttr);
    }
    /**
     * Sets attributes on the anchor element that are coming from pvdNativeAnchorAttributes
     * @param prevAttr optional, if user updates pvdNativeAnchorAttributes, this will remove/update existing attributes
     */
    setNativeAnchorAttributes(prevAttr) {
        const targetElement = this.querySelector('a');
        this.setNativeAttributes(this.pvdNativeAnchorAttributes, ANCHOR_PROTECTED_ATTR_MAP, targetElement, prevAttr);
    }
    // #endregion
    // #region LitElement lifecycles
    updated(changedProperties) {
        super.updated(changedProperties);
        let prevAttr;
        if (this.pvdNativeButtonAttributes) {
            prevAttr = changedProperties.get('pvdNativeButtonAttributes');
            // if native attributes are being set, go through the logic of applying attributes to target element
            this.setNativeButtonAttributes(prevAttr);
        }
        if (this.pvdNativeAnchorAttributes) {
            prevAttr = changedProperties.get('pvdNativeAnchorAttributes');
            // if native attributes are being set, go through the logic of applying attributes to target element
            this.setNativeAnchorAttributes(prevAttr);
        }
    }
    renderButtonContents() {
        // unable to dynamically create an named s-slot (e.g., <s-slot name="icon-${position}"></s-slot> doesn't work)
        const leftIcon = this.renderLeftIcon();
        const rightIcon = this.renderRightIcon();
        const text = this.renderText();
        return x `<div class="pvd-button__contents">${leftIcon}${text}${rightIcon}</div>`;
    }
    renderText() {
        return this.hasText
            ? x `<span class="pvd-button__text"><s-slot>${this.pvdText}</s-slot></span>`
            : x `<s-slot></s-slot>`;
    }
    renderLeftIcon() {
        return this.hasIconLeft
            ? x `<span class="pvd-button__icon pvd-button__icon--left"><s-slot name="icon-left" @slotchange="${this.onSlotChangeHandler}">${this.renderIcon(this.pvdIconLeft)}</s-slot></span>`
            : x `<s-slot name="icon-left" @slotchange="${this.onSlotChangeHandler}"></s-slot>`;
    }
    renderRightIcon() {
        return this.hasIconRight
            ? x `<span class="pvd-button__icon pvd-button__icon--right"><s-slot name="icon-right" @slotchange="${this.onSlotChangeHandler}">${this.renderIcon(this.pvdIconRight)}</s-slot></span>`
            : x `<s-slot name="icon-right" @slotchange="${this.onSlotChangeHandler}"></s-slot>`;
    }
    renderIcon(iconName) {
        if (!iconName) {
            return A;
        }
        return this.scopeTemplate `<pvd-scoped-icon
      pvd-name="${iconName}"
      pvd-size="${this.pvdSize === StandardSize.large ? Size.medium : Size.small}"
    ></pvd-scoped-icon>`;
    }
    render() {
        const classMap = {
            default: `${this.constructor.namespace}-button-root`,
            modifierPrefix: 'pvd-button',
            modifiers: [
                {
                    condition: this.pvdIconOnly || (!this.hasText && this.hasIcon),
                    className: 'icon-only',
                },
                {
                    condition: this.pvdHref !== undefined,
                    className: 'anchor',
                },
                {
                    condition: this.pvdFullWidth,
                    className: 'full-width',
                },
                {
                    condition: this.hasIcon,
                    className: 'with-icon',
                },
                {
                    condition: this.pvdSize !== StandardSize.medium,
                    className: `${this.pvdSize}`,
                },
                {
                    condition: true,
                    className: this.pvdVariant,
                },
            ],
        };
        if (!this.pvdHref) {
            return x `<button ?disabled="${this.pvdDisabled}" class="${getClassName(classMap)}" id="${l(this.pvdId)}" type="${this.pvdType}">${this.renderButtonContents()}</button>`;
        }
        else {
            return x `<a class="${getClassName(classMap)}" href="${this.pvdHref}" id="${l(this.pvdId)}">${this.renderButtonContents()}</a>`;
        }
    }
}
//#region Component configuration
PvdButton.componentName = 'button';
__decorate([
    n({
        type: Boolean,
        reflect: true,
        attribute: 'pvd-disabled',
        ...PvdSlotifyBaseComponent.booleanAttribute('pvd-disabled'),
    }),
    __metadata("design:type", Boolean)
], PvdButton.prototype, "pvdDisabled", void 0);
__decorate([
    n({
        type: Boolean,
        reflect: true,
        attribute: 'pvd-full-width',
        ...PvdSlotifyBaseComponent.booleanAttribute('pvd-full-width'),
    }),
    __metadata("design:type", Boolean)
], PvdButton.prototype, "pvdFullWidth", void 0);
__decorate([
    n({ type: String, reflect: true, attribute: 'pvd-href' }),
    __metadata("design:type", String)
], PvdButton.prototype, "pvdHref", void 0);
__decorate([
    n({ type: String, reflect: true, attribute: 'pvd-icon-right' }),
    __metadata("design:type", String)
], PvdButton.prototype, "pvdIconRight", void 0);
__decorate([
    n({ type: String, reflect: true, attribute: 'pvd-icon-left' }),
    __metadata("design:type", String)
], PvdButton.prototype, "pvdIconLeft", void 0);
__decorate([
    n({
        type: Boolean,
        reflect: true,
        attribute: 'pvd-icon-only',
        ...PvdSlotifyBaseComponent.booleanAttribute('pvd-icon-only'),
    }),
    __metadata("design:type", Boolean)
], PvdButton.prototype, "pvdIconOnly", void 0);
__decorate([
    n({ type: String, reflect: true, attribute: 'pvd-id' }),
    __metadata("design:type", String)
], PvdButton.prototype, "pvdId", void 0);
__decorate([
    n({ type: Object, attribute: 'pvd-native-anchor-attributes' }),
    __metadata("design:type", Object)
], PvdButton.prototype, "pvdNativeAnchorAttributes", void 0);
__decorate([
    n({ type: Object, attribute: 'pvd-native-button-attributes' }),
    __metadata("design:type", Object)
], PvdButton.prototype, "pvdNativeButtonAttributes", void 0);
__decorate([
    n({
        type: String,
        reflect: true,
        attribute: 'pvd-size',
        ...PvdSlotifyBaseComponent.hasChanged(StandardSize),
    }),
    __metadata("design:type", Number)
], PvdButton.prototype, "pvdSize", void 0);
__decorate([
    n({ type: String, reflect: true, attribute: 'pvd-text' }),
    __metadata("design:type", String)
], PvdButton.prototype, "pvdText", void 0);
__decorate([
    n({
        type: String,
        reflect: true,
        attribute: 'pvd-type',
        ...PvdSlotifyBaseComponent.hasChanged(ButtonType),
    }),
    __metadata("design:type", String)
], PvdButton.prototype, "pvdType", void 0);
__decorate([
    n({
        type: String,
        reflect: true,
        attribute: 'pvd-variant',
        ...PvdSlotifyBaseComponent.hasChanged(ButtonVariant),
    }),
    __metadata("design:type", String)
], PvdButton.prototype, "pvdVariant", void 0);

export { ANCHOR_PROTECTED_ATTR_MAP, BUTTON_PROTECTED_ATTR_MAP, ButtonType, ButtonVariant, DEFAULT_DISABLED, DEFAULT_FULL_WIDTH, DEFAULT_ICON_ONLY, DEFAULT_SIZE, DEFAULT_TYPE, DEFAULT_VARIANT, PvdButton };
//# sourceMappingURL=pvd-button.js.map
